import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Badge, Input, Label, Select, SelectItem } from '@fyos/components';
import {
  ArrowLeft,
  Pencil,
  Trash2,
  Columns3,
  Calendar,
  Activity,
  ThumbsUp,
  ThumbsDown,
  ShieldCheck,
  Users,
  Link2,
  AlertTriangle,
  ExternalLink,
  Paperclip,
  Upload,
  SmilePlus,
  Eye,
  EyeOff,
  MessageSquare,
  Loader2,
} from 'lucide-react';
import { useWorkspaceMetadataStore } from '@/stores/workspaceMetadataStore';
import { Item, ItemReaction, itemApi } from '@/lib/itemApi';
import EmojiPicker from '@/components/EmojiPicker';
import { useAuthStore } from '@/stores/authStore';
import { attachmentApi, Attachment, formatFileSize, isImageMime } from '@/lib/attachmentApi';
import { useS3UrlRefresher } from '@/hooks/useS3UrlRefresher';
import CommentSection from '@/components/CommentSection';
import RichTextEditor from '@/components/RichTextEditor';
import MarkdownContent from '@/components/MarkdownContent';
import ConfirmDialog from '@/components/ConfirmDialog';
import AppNav from '@/components/AppNav';

export default function ItemDetailPage() {
  const { workspaceId, itemId } = useParams<{ workspaceId: string; itemId: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  useS3UrlRefresher(workspaceId);

  // Subscribe to shared metadata store for categories and tags
  const categories = useWorkspaceMetadataStore((state) => state.categories);
  const tags = useWorkspaceMetadataStore((state) => state.tags);

  const [item, setItem] = useState<Item | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [reactions, setReactions] = useState<ItemReaction[]>([]);
  const [isWatching, setIsWatching] = useState(false);
  const [watcherCount, setWatcherCount] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!workspaceId || !itemId) return;
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        // Fetch item data
        const itemData = await itemApi.getById(workspaceId, itemId);
        setItem(itemData);
        setTitle(itemData.title);
        setDescription(itemData.description || '');
        setCategoryId(itemData.category_id || null);
        setSelectedTags(itemData.tags?.map(t => t.id) || []);
        setReactions(itemData.reactions || []);
        setIsWatching(itemData.watchers?.some(w => w.id === user?.id) || false);
        setWatcherCount(itemData.watcher_count || 0);
        
        // Fetch metadata (shared store)
        await useWorkspaceMetadataStore.getState().fetchAll(workspaceId);
      } catch (err) {
        console.error('Failed to fetch item:', err);
        setError('Item not found or you do not have access.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [workspaceId, itemId, user?.id]);

  useEffect(() => {
    if (!workspaceId || !itemId) return;
    attachmentApi.getAll(workspaceId, itemId).then(setAttachments).catch(() => {});
  }, [workspaceId, itemId]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !workspaceId || !itemId) return;
    try {
      setUploading(true);
      const attachment = await attachmentApi.upload(workspaceId, itemId, file);
      setAttachments(prev => [attachment, ...prev]);
    } catch (err) {
      console.error('Failed to upload file:', err);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteAttachment = async (attachmentId: string) => {
    if (!workspaceId || !itemId) return;
    try {
      await attachmentApi.delete(workspaceId, itemId, attachmentId);
      setAttachments(prev => prev.filter(a => a.id !== attachmentId));
    } catch (err) {
      console.error('Failed to delete attachment:', err);
    }
  };

  const handleSave = async () => {
    if (!workspaceId || !item) return;
    try {
      setSaving(true);
      const updated = await itemApi.update(workspaceId, item.id, {
        title,
        description: description || undefined,
        category_id: categoryId || undefined,
        tag_ids: selectedTags.length > 0 ? selectedTags : undefined,
      });
      setItem(updated);
      setIsEditing(false);
    } catch (err) {
      console.error('Failed to update item:', err);
    } finally {
      setSaving(false);
    }
  };

  const toggleTag = (tagId: string) => {
    setSelectedTags(prev =>
      prev.includes(tagId) ? prev.filter(id => id !== tagId) : [...prev, tagId]
    );
  };

  const cancelEditing = () => {
    if (!item) return;
    setIsEditing(false);
    setTitle(item.title);
    setDescription(item.description || '');
    setCategoryId(item.category_id || null);
    setSelectedTags(item.tags?.map(t => t.id) || []);
  };

  const handleDelete = async () => {
    if (!workspaceId || !item) return;
    try {
      setDeleting(true);
      await itemApi.delete(workspaceId, item.id);
      navigate(`/roadmap/${workspaceId}`);
    } catch (err) {
      console.error('Failed to delete item:', err);
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col bg-gray-50 dark:bg-gray-900">
        <AppNav />
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </div>
    );
  }

  if (error || !item) {
    return (
      <div className="flex min-h-screen flex-col bg-gray-50 dark:bg-gray-900">
        <AppNav />
        <div className="flex flex-1 flex-col items-center justify-center gap-4">
          <p className="text-gray-500 dark:text-gray-400">{error || 'Item not found.'}</p>
          <Button variant="outline" onClick={() => navigate(`/roadmap/${workspaceId}`)}>
            <ArrowLeft className="h-4 w-4" />
            Back to Roadmap
          </Button>
        </div>
      </div>
    );
  }

  const category = categories.find(c => c.id === item.category_id);

  const columnColors: Record<string, { bg: string; text: string }> = {
    now: { bg: 'bg-amber-50 dark:bg-amber-950/30', text: 'text-amber-500' },
    next: { bg: 'bg-blue-50 dark:bg-blue-950/30', text: 'text-blue-500' },
    later: { bg: 'bg-violet-50 dark:bg-violet-950/30', text: 'text-violet-500' },
    shipped: { bg: 'bg-emerald-50 dark:bg-emerald-950/30', text: 'text-emerald-500' },
    killed: { bg: 'bg-gray-100 dark:bg-gray-700/50', text: 'text-gray-500' },
  };
  const colColor = columnColors[item.board_column] || columnColors.killed;

  return (
    <div className="flex min-h-screen flex-col bg-gray-50 dark:bg-gray-900">
      <AppNav />

      <div className="max-w-8xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-6">
        {/* Back button */}
        <div className="mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/roadmap/${workspaceId}`)}>
            <ArrowLeft className="h-4 w-4" />
            Back to Roadmap
          </Button>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Main content */}
          <div className="flex-1 min-w-0">
            <div className="rounded-2xl bg-white shadow-sm border border-gray-200 dark:bg-gray-800 dark:border-gray-700 p-6 sm:p-8">
              {/* Header */}
              <div className="mb-6">
                <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{item.title}</h1>
              </div>

              {!isEditing && (
                <div className="mb-6 flex flex-wrap items-center justify-center gap-1">
                  <Button
                    variant={isWatching ? 'primary' : 'secondary'}
                    size="sm"
                    iconOnlyOnMobile
                    onClick={async () => {
                      try {
                        const result = isWatching
                          ? await itemApi.unwatch(workspaceId!, item.id)
                          : await itemApi.watch(workspaceId!, item.id);
                        if (result) {
                          setIsWatching(result.watching ?? !isWatching);
                          setWatcherCount(result.watcher_count ?? watcherCount);
                        } else {
                          setIsWatching(!isWatching);
                        }
                      } catch (err) {
                        console.error('Failed to toggle watch:', err);
                      }
                    }}
                  >
                    {isWatching ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                    {isWatching ? 'Unwatch' : 'Watch'}{watcherCount > 0 ? ` (${watcherCount})` : ''}
                  </Button>
                  <Button variant="secondary" size="sm" iconOnlyOnMobile onClick={() => setIsEditing(true)}>
                    <Pencil className="h-3.5 w-3.5" />
                    Edit
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    iconOnlyOnMobile
                    disabled={deleting}
                    onClick={() => setShowDeleteConfirm(true)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    {deleting ? 'Deleting...' : 'Delete'}
                  </Button>
                </div>
              )}

              {isEditing ? (
                <div className="space-y-4 mb-6">
                  <div>
                    <Label htmlFor="edit-title">Title</Label>
                    <Input
                      id="edit-title"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                    />
                  </div>

                  <div>
                    <Label htmlFor="edit-description">Description</Label>
                    <RichTextEditor
                      content={description}
                      onChange={setDescription}
                      placeholder="Add a description..."
                      minHeight="300px"
                    />
                  </div>

                  {categories.length > 0 && (
                    <div>
                      <Label>Category</Label>
                      <Select
                        value={categoryId || '__none__'}
                        onValueChange={(val) => setCategoryId(val === '__none__' ? null : val)}
                        placeholder="Select category..."
                      >
                        <SelectItem value="__none__">None</SelectItem>
                        {categories.map(cat => (
                          <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                        ))}
                      </Select>
                    </div>
                  )}

                  {tags.length > 0 && (
                    <div>
                      <Label>Tags</Label>
                      <div className="flex flex-wrap gap-2">
                        {tags.map(tag => (
                          <button
                            key={tag.id}
                            type="button"
                            onClick={() => toggleTag(tag.id)}
                          >
                            <Badge
                              variant={selectedTags.includes(tag.id) ? 'primary' : 'gray'}
                              size="md"
                              className="cursor-pointer transition-colors"
                            >
                              {tag.name}
                            </Badge>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <Button onClick={handleSave} disabled={saving}>
                      {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                    <Button variant="outline" onClick={cancelEditing}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="mb-6">
                  {item.description && (
                    <MarkdownContent
                      content={item.description}
                      className="text-gray-700 dark:text-gray-300 mb-6 prose prose-base dark:prose-invert max-w-none"
                    />
                  )}

                  {(category || (item.tags && item.tags.length > 0)) && (
                    <div className="flex flex-wrap gap-2 mb-6">
                      {category && (
                        <Badge
                          size="md"
                          style={{ backgroundColor: category.color + '20', color: category.color }}
                        >
                          {category.name}
                        </Badge>
                      )}
                      {item.tags && item.tags.map(tag => (
                        <Badge
                          key={tag.id}
                          size="md"
                          style={{ backgroundColor: tag.color + '20', color: tag.color }}
                        >
                          {tag.name}
                        </Badge>
                      ))}
                    </div>
                  )}

                  <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-4">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      <div className="flex items-center gap-2">
                        <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${colColor.bg}`}>
                          <Columns3 className={`h-4 w-4 ${colColor.text}`} />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Column</p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white capitalize">{item.board_column}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 dark:bg-blue-950/30">
                          <Calendar className="h-4 w-4 text-blue-500" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Created</p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">{new Date(item.created_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                      {item.last_activity_at && (
                        <div className="flex items-center gap-2">
                          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-green-50 dark:bg-green-950/30">
                            <Activity className="h-4 w-4 text-green-500" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Last Activity</p>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{new Date(item.last_activity_at).toLocaleDateString()}</p>
                          </div>
                        </div>
                      )}
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              const result = await itemApi.vote(workspaceId!, item.id, 'up');
                              setItem(prev => prev ? { ...prev, vote_count: result.vote_count, user_vote: result.user_vote } : prev);
                            } catch (err) {
                              console.error('Failed to vote:', err);
                            }
                          }}
                          className={`flex h-8 w-8 items-center justify-center rounded-lg transition-colors hover:bg-gray-100 dark:hover:bg-gray-700 ${item.user_vote === 1 ? 'bg-brand-50 dark:bg-brand-950/30 ring-1 ring-brand-300 dark:ring-brand-600' : 'bg-purple-50 dark:bg-purple-950/30'}`}
                          title={item.user_vote === 1 ? 'Remove upvote' : 'Upvote'}
                        >
                          <ThumbsUp className={`h-4 w-4 ${item.user_vote === 1 ? 'text-brand-500' : 'text-purple-500'}`} />
                        </button>
                        <div className="text-center px-1">
                          <p className="text-xs text-gray-500 dark:text-gray-400">Votes</p>
                          <p className={`text-sm font-medium ${item.user_vote === 1 ? 'text-brand-500' : item.user_vote === -1 ? 'text-red-500' : 'text-gray-900 dark:text-white'}`}>{item.vote_count}</p>
                        </div>
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              const result = await itemApi.vote(workspaceId!, item.id, 'down');
                              setItem(prev => prev ? { ...prev, vote_count: result.vote_count, user_vote: result.user_vote } : prev);
                            } catch (err) {
                              console.error('Failed to vote:', err);
                            }
                          }}
                          className={`flex h-8 w-8 items-center justify-center rounded-lg transition-colors hover:bg-gray-100 dark:hover:bg-gray-700 ${item.user_vote === -1 ? 'bg-red-50 dark:bg-red-950/30 ring-1 ring-red-300 dark:ring-red-600' : 'bg-purple-50 dark:bg-purple-950/30'}`}
                          title={item.user_vote === -1 ? 'Remove downvote' : 'Downvote'}
                        >
                          <ThumbsDown className={`h-4 w-4 ${item.user_vote === -1 ? 'text-red-500' : 'text-purple-500'}`} />
                        </button>
                      </div>
                    </div>
                    {item.auto_kill_exempt ? (
                      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                        <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
                          <ShieldCheck className="h-4 w-4" />
                          <span className="text-sm font-medium">Auto-kill exempt</span>
                        </div>
                      </div>
                    ) : null}
                  </div>

                  {(item.description?.length || 0) > 10240 && (
                    <div className="mt-6 flex flex-wrap items-center justify-center gap-1">
                      <Button
                        variant={isWatching ? 'primary' : 'secondary'}
                        size="sm"
                        iconOnlyOnMobile
                        onClick={async () => {
                          try {
                            const result = isWatching
                              ? await itemApi.unwatch(workspaceId!, item.id)
                              : await itemApi.watch(workspaceId!, item.id);
                            if (result) {
                              setIsWatching(result.watching ?? !isWatching);
                              setWatcherCount(result.watcher_count ?? watcherCount);
                            } else {
                              setIsWatching(!isWatching);
                            }
                          } catch (err) {
                            console.error('Failed to toggle watch:', err);
                          }
                        }}
                      >
                        {isWatching ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                        {isWatching ? 'Unwatch' : 'Watch'}{watcherCount > 0 ? ` (${watcherCount})` : ''}
                      </Button>
                      <Button variant="secondary" size="sm" iconOnlyOnMobile onClick={() => setIsEditing(true)}>
                        <Pencil className="h-3.5 w-3.5" />
                        Edit
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        iconOnlyOnMobile
                        disabled={deleting}
                        onClick={() => setShowDeleteConfirm(true)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        {deleting ? 'Deleting...' : 'Delete'}
                      </Button>
                    </div>
                  )}

                  {/* Item State */}
                  {item.item_state && item.item_state !== 'active' && (
                    <div className="mt-4 flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-500" />
                      <Badge variant={item.item_state === 'blocked' ? 'error' : item.item_state === 'at_risk' ? 'warning' : 'gray'}>
                        {item.item_state.replace(/_/g, ' ')}
                      </Badge>
                    </div>
                  )}

                  {/* Assignees */}
                  {item.assignees && item.assignees.length > 0 && (
                    <div className="mt-6">
                      <div className="flex items-center gap-2 mb-2">
                        <Users className="h-4 w-4 text-gray-400" />
                        <span className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Assignees</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {item.assignees.map(a => (
                          <div key={a.id} className="flex items-center gap-2 rounded-full bg-gray-100 dark:bg-gray-700 px-3 py-1">
                            <div className="h-5 w-5 rounded-full bg-brand-200 dark:bg-brand-800 flex items-center justify-center text-[10px] font-medium text-brand-700 dark:text-brand-300">
                              {a.name?.charAt(0)?.toUpperCase() || '?'}
                            </div>
                            <span className="text-sm text-gray-700 dark:text-gray-300">{a.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Dependencies */}
                  {((item.blocks && item.blocks.length > 0) || (item.blocked_by && item.blocked_by.length > 0)) && (
                    <div className="mt-6">
                      <div className="flex items-center gap-2 mb-2">
                        <Link2 className="h-4 w-4 text-gray-400" />
                        <span className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Dependencies</span>
                      </div>
                      {item.blocks && item.blocks.length > 0 && (
                        <div className="mb-2">
                          <span className="text-xs text-gray-500 dark:text-gray-400">Blocks:</span>
                          <div className="flex flex-wrap gap-1.5 mt-1">
                            {item.blocks.map(dep => (
                              <Badge key={dep.dependency_id} variant="error">
                                {dep.title}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      {item.blocked_by && item.blocked_by.length > 0 && (
                        <div>
                          <span className="text-xs text-gray-500 dark:text-gray-400">Blocked by:</span>
                          <div className="flex flex-wrap gap-1.5 mt-1">
                            {item.blocked_by.map(dep => (
                              <Badge key={dep.dependency_id} variant="warning">
                                {dep.title}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* External Links */}
                  {item.external_links && item.external_links.length > 0 && (
                    <div className="mt-6">
                      <div className="flex items-center gap-2 mb-2">
                        <ExternalLink className="h-4 w-4 text-gray-400" />
                        <span className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Links</span>
                      </div>
                      <div className="space-y-1.5">
                        {item.external_links.map((link, idx) => (
                          <a
                            key={idx}
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-sm text-brand-600 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300 truncate"
                          >
                            <ExternalLink className="h-3.5 w-3.5 flex-shrink-0" />
                            {link.label || link.url}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Reactions */}
                  <div className="mt-6">
                    <div className="flex items-center gap-2 mb-2">
                      <SmilePlus className="h-4 w-4 text-gray-400" />
                      <span className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Reactions</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {reactions.map(r => (
                        <button
                          key={r.emoji}
                          type="button"
                          onClick={async () => {
                            try {
                              const updated = await itemApi.react(workspaceId!, item.id, r.emoji);
                              setReactions(updated);
                            } catch (err) {
                              console.error('Failed to toggle reaction:', err);
                            }
                          }}
                          className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-colors ${
                            user && r.user_ids.includes(user.id)
                              ? 'border-brand-300 bg-brand-50 dark:border-brand-700 dark:bg-brand-950/30'
                              : 'border-gray-200 bg-gray-50 hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700'
                          }`}
                        >
                          <span className="text-base leading-none">{r.emoji}</span>
                          <span className="text-xs font-medium text-gray-600 dark:text-gray-300">{r.count}</span>
                        </button>
                      ))}
                      <EmojiPicker onSelect={async (emoji) => {
                        try {
                          const updated = await itemApi.react(workspaceId!, item.id, emoji);
                          setReactions(updated);
                        } catch (err) {
                          console.error('Failed to add reaction:', err);
                        }
                      }} />
                    </div>
                  </div>
                </div>
              )}

              {/* Attachments */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Paperclip className="h-4 w-4 text-gray-400" />
                    <span className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Attachments {attachments.length > 0 && `(${attachments.length})`}
                    </span>
                  </div>
                  <div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      onChange={handleFileUpload}
                      className="hidden"
                      accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.md,.zip,.tar,.gz,.json,.xml"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                    >
                      <Upload className="h-3.5 w-3.5" />
                      {uploading ? 'Uploading...' : 'Upload'}
                    </Button>
                  </div>
                </div>
                {attachments.length > 0 && (
                  <div className="space-y-2">
                    {attachments.map(att => (
                      <div
                        key={att.id}
                        className="flex items-center gap-3 p-2 rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800"
                      >
                        {isImageMime(att.mime_type) ? (
                          <img
                            src={att.url}
                            alt={att.original_name}
                            className={`h-10 w-10 rounded object-cover flex-shrink-0 ${att.path ? 's3-asset' : ''}`}
                            {...(att.path ? { 'data-s3-path': att.path, 'data-s3-expires': Date.now().toString() } : {})}
                          />
                        ) : (
                          <div className="h-10 w-10 rounded bg-gray-200 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                            <Paperclip className="h-4 w-4 text-gray-500" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <a
                            href={att.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`text-sm font-medium text-gray-900 dark:text-white hover:text-brand-600 dark:hover:text-brand-400 truncate block ${att.path ? 's3-asset' : ''}`}
                            {...(att.path ? { 'data-s3-path': att.path, 'data-s3-expires': Date.now().toString() } : {})}
                          >
                            {att.original_name}
                          </a>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {formatFileSize(att.size)}
                          </span>
                        </div>
                        <button
                          onClick={() => handleDeleteAttachment(att.id)}
                          className="p-1 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                          title="Delete attachment"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Comments sidebar */}
          <div className="lg:w-[380px] lg:flex-shrink-0">
            <div className="rounded-2xl bg-white shadow-sm border border-gray-200 dark:bg-gray-800 dark:border-gray-700 p-5">
              <div className="flex items-center gap-2 mb-4">
                <MessageSquare className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                  Comments{item.comment_count > 0 ? ` (${item.comment_count})` : ''}
                </h3>
              </div>
              <CommentSection 
                workspaceId={workspaceId!} 
                itemId={item.id} 
                showHeader={false}
                onCommentCountChange={(count) => {
                  setItem(prev => prev ? { ...prev, comment_count: count } : prev);
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Delete Item"
        description="Are you sure you want to delete this item? This action cannot be undone."
        confirmLabel="Delete"
        variant="danger"
        loading={deleting}
        onConfirm={handleDelete}
      />
    </div>
  );
}
